import type { SubmitStepInfo } from "../components/action/types";

import apiUrls from "../../../../api/urls";
import { customApiCall } from "../../../../utils/axios";
import { cloneFormDataForSubmit, withFileSectionUrl, withImageUrl } from "../lib/formStateUpload";
import { getUploadDestPath } from "../lib/uploadPaths";
import {
  type EnergyAnalysisFileSection,
  type FileUploadProgress,
  type FileWithInfoLocal,
  isFileFromLocal,
  type SingleBuildingFormState,
} from "../model/types";

export const uploadSingleFile = async (
  file: File,
  destPath: string,
  options?: {
    onProgress?: (event: FileUploadProgress) => void;
    signal?: AbortSignal;
  }
): Promise<{
  contentType: string;
  lastModified: string;
  name: string;
  path: string;
  size: number;
  url: string;
}> => {
  const { onProgress, signal } = options ?? {};

  try {
    const data = await customApiCall.upload({
      data: { destPath, file, overwrite: "true" },
      onUploadProgress: onProgress
        ? ({ percentage }: { percentage: number }) => {
            onProgress({ percentage, status: "progress" });
          }
        : undefined,
      signal,
      url: apiUrls.userFiles.freeUpload,
    });
    onProgress?.({ percentage: 100, status: "completed" });
    return data.data;
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : undefined;
    onProgress?.({ errorMessage, percentage: 0, status: "error" });
    throw e;
  }
};

export const uploadFilesAndImages = async ({
  formData,
  onUploadRowProgress,
  signal,
  submitDateTime,
  userId,
}: {
  formData: SingleBuildingFormState;
  onUploadRowProgress: (
    list: "files" | "images",
    file: FileWithInfoLocal,
    progress: { errorMessage?: string | undefined; percentage: number; status: SubmitStepInfo["status"] }
  ) => void;
  signal: AbortSignal;
  submitDateTime: Date;
  userId: string;
}) => {
  let formDataWithUrls = cloneFormDataForSubmit(formData);

  const localImages = formData.images.filter(isFileFromLocal);

  for (let i = 0; i < localImages.length; i++) {
    const file = localImages[i];
    try {
      const uploadDestPath = getUploadDestPath({
        datetime: submitDateTime,
        fileType: "image",
        userId,
      });

      const resp = await uploadSingleFile(file.file, uploadDestPath, {
        onProgress: ({ percentage, status }) => {
          onUploadRowProgress("images", file, { percentage, status });
        },
        signal,
      });
      const uploadedUrl = resp.path;
      formDataWithUrls = withImageUrl(formDataWithUrls, file, uploadedUrl);
    } catch {
      //
    }
  }

  for (const [section, files] of Object.entries(formData.files ?? {})) {
    const uploadDestPath = getUploadDestPath({
      datetime: submitDateTime,
      fileSection: section,
      fileType: "file",
      userId,
    });
    for (const file of files) {
      try {
        const resp = await uploadSingleFile(file.file, uploadDestPath, {
          onProgress: ({ percentage, status }) => {
            onUploadRowProgress("files", file, { percentage, status });
          },
          signal,
        });
        const uploadedPath = resp.path;
        formDataWithUrls = withFileSectionUrl(
          formDataWithUrls,
          section as EnergyAnalysisFileSection,
          file,
          uploadedPath
        );
      } catch {
        //
      }
    }
  }

  return formDataWithUrls;
};
